import { get } from "lodash";
import { GetServerSideProps, InferGetServerSidePropsType } from "next";
import React from "react";
import SEOTags from "~/components/SEOTags";
import { Identifiers } from "~/constants/enum";
import { QUERY_GET_CMS_PAGE } from "~/data/cms/gql";
import { QUERY_GET_COLLECTIONS_BLOCKS } from "~/data/collections";
import {
  GET_SLIDER_GQL,
  QUERY_GET_CMS_BLOCKS,
  QUERY_GET_STORE_CONFIG_GQL,
  QUERY_RELATED_PRODUCT_BY_SKUS,
  QUERY_GET_ALL_REVIEWS_ONLY_IMG,
} from "~/data/home";
import { QUERY_GET_MEGA_MENU_GQL } from "~/data/mega-menu/gql";
import { GET_SCHEMA_HOME } from "~/data/schemas/schema.gql";
import { useMobile } from "~/hooks/useMobile";
import Layout from "~/layouts/ThemeLayout";
import ReMaketing, { REMARKETING_HOME } from "~/services/tracking/ReMaketing";
import styles from "~/styles/pages/Home.module.scss";
import HomeSchema from "~/tracking-pages/home.schema";
import { initializeSSRApollo } from "~/utils/apollo-client";
import { getPageStruct, renderSectionByKey } from "~/utils/pageTwo";
import { getTheme } from "~/utils/theme";
import { createTimeoutPromise } from "~/utils/helper";

const SSRIdentifiers = [
  Identifiers.HomeBrand,
  Identifiers.CategoriesSection,
  Identifiers.PromotionBanner,
  Identifiers.ProductMatching,
  Identifiers.TopCategories,
  Identifiers.FeatureProduct,
  Identifiers.Introduction,
  Identifiers.FeaturedProductV2,
  Identifiers.FeaturedProductV2_2,
  Identifiers.FeaturedProductV2_3,
  Identifiers.FirstBanner,
  Identifiers.Cards,
  Identifiers.SecondBanner,
  Identifiers.Artist,
  Identifiers.ThirdBanner,
  Identifiers.Organization,
  Identifiers.BenefitsSection,
  Identifiers.FollowUs,
  Identifiers.NewCollection,
  Identifiers.TrendingBanner,
  Identifiers.Recommended,
]; // Add all identifiers that need to be fetched in SSR

const pageStruct = getPageStruct(getTheme(), "home");
const sections: any = pageStruct?.children
  ?.sort((a: any, b: any) => a.order - b.order)
  .map((item: any) => {
    const Section = renderSectionByKey(item.id);
    return {
      id: item.id,
      order: item.order,
      dataKey: item.dataKey,
      identifier: item.identifier,
      component: Section ?? null,
    };
  });

const Home = ({
  meta,
  configSchema,
  serverCmsBlocks,
  dataMegaMenu,
  storeConfig,
  sliderItems,
  bestSellerRelatedProductsBySkus,
  dataCollectionSsr,
  dataCollectionV2Ssr,
  mostPopularRelatedProductsBySkus,
  trendingRelatedProductsBySkus,
  siteReviews,
  isMobile,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const newTitle = [
    process.env.NEXT_PUBLIC_SEO_DEFAULT_BRAND,
    meta?.meta_title || process.env.NEXT_PUBLIC_SEO_DEFAULT_TITLE,
  ]
    .filter((i) => !!i)
    .join(" | ");

  // if (!isClient) return null;

  let linkCanonical = process.env.NEXT_PUBLIC_URL ?? "";

  return (
    <>
      <h1 className="hidden">{newTitle}</h1>
      <Layout
        isUseMinHeight
        dataMegaMenu={dataMegaMenu}
        storeConfig={storeConfig}
      >
        <SEOTags
          metaTitle={newTitle}
          description={meta?.meta_description}
          keywords={meta?.meta_keywords}
        >
          <link rel="canonical" href={linkCanonical} data-vm-ssr="true" />
        </SEOTags>
        <HomeSchema config={configSchema} />
        <ReMaketing gtagType={REMARKETING_HOME} />
        <div className={styles.homepage}>
          {sections.map((section: any, index: number) => {
            const Section = section.component;
            return !!Section ? (
              <Section
                data={serverCmsBlocks?.[section.identifier]}
                isMobile={isMobile}
                {...(sliderItems && section.identifier === Identifiers.Hero
                  ? { sliderItems }
                  : {})}
                {...(bestSellerRelatedProductsBySkus &&
                section.identifier === Identifiers.BestSeller
                  ? { bestSellerRelatedProductsBySkus }
                  : {})}
                {...(dataCollectionSsr &&
                section.identifier === Identifiers.FeatureProduct
                  ? { dataCollectionSsr }
                  : {})}
                {...(dataCollectionV2Ssr &&
                (section.identifier === Identifiers.FeaturedProductV2 ||
                  section.identifier === Identifiers.FeaturedProductV2_2 ||
                  section.identifier === Identifiers.FeaturedProductV2_3)
                  ? { dataCollectionSsr: dataCollectionV2Ssr }
                  : {})}
                {...(mostPopularRelatedProductsBySkus &&
                section.identifier === Identifiers.MostPopular
                  ? { mostPopularRelatedProductsBySkus }
                  : {})}
                {...(trendingRelatedProductsBySkus &&
                section.identifier === Identifiers.TrendingBanner
                  ? { trendingRelatedProductsBySkus }
                  : {})}
                {...((siteReviews &&
                  section.identifier === Identifiers.Testimonial) ||
                section.identifier === Identifiers.HomePageReviews
                  ? { siteReviews }
                  : {})}
              />
            ) : null;
          })}
        </div>
      </Layout>
    </>
  );
};

export default Home;

export const getServerSideProps: GetServerSideProps = async ({ req }) => {
  const isMobile = useMobile(req.headers["user-agent"]);
  const apolloClient = initializeSSRApollo({
    httpLinkProps: { useGETForQueries: true },
  });
  try {
    const [
      dataRes,
      dataSchemaRes,
      fetchSliderRes,
      dataMegaMenuRes,
      responseRes,
      siteReviewsRes,
      blockDataRes,
    ] = await Promise.allSettled([
      apolloClient.query({
        query: QUERY_GET_CMS_PAGE,
        variables: {
          identifier: "home",
        },
      }),
      apolloClient.query({
        query: GET_SCHEMA_HOME,
      }),
      apolloClient.query({
        query: GET_SLIDER_GQL,
        fetchPolicy: "network-only",
        context: {
          fetchOptions: { method: "GET" },
        },
      }),
      apolloClient.query({
        query: QUERY_GET_MEGA_MENU_GQL,
        variables: {
          identifiers: Identifiers.MainMenu,
        },
        context: { fetchOptions: { method: "GET" } },
      }),
      apolloClient.query({
        query: QUERY_GET_STORE_CONFIG_GQL,
      }),
      apolloClient.query({
        query: QUERY_GET_ALL_REVIEWS_ONLY_IMG,
        variables: {
          images: true,
          pageSize: 10,
        },
        context: { fetchOptions: { method: "GET" } },
      }),
      apolloClient.query({
        query: QUERY_GET_CMS_BLOCKS,
        variables: { identifiers: SSRIdentifiers },
        context: { fetchOptions: { method: "GET" } },
      }),
    ]);

    const data = (dataRes as any)?.value?.data;
    const dataSchema = (dataSchemaRes as any)?.value?.data;
    const fetchSlider = (fetchSliderRes as any)?.value;
    const dataMegaMenu = (dataMegaMenuRes as any)?.value?.data;
    const siteReviews = (siteReviewsRes as any)?.value?.data;
    const blockData = (blockDataRes as any)?.value?.data;
    const response = (responseRes as any)?.value;

    const storeConfig = get(response, "data.storeConfig", {
      frontend_config_logo: null,
      best_seller_group_id: null,
      popular_group_id: null,
      trending_group_id: null,
      store_notice_enable: null,
    });

    const [
      queryProductRelatedRes,
      mostPopularProductsRes,
      trendingProductsRes,
    ] = await Promise.allSettled([
      apolloClient.query({
        query: QUERY_RELATED_PRODUCT_BY_SKUS,
        variables: {
          ruleId: Number(storeConfig.best_seller_group_id),
          limit: 48,
        },
        context: { fetchOptions: { method: "GET" } },
      }),
      apolloClient.query({
        query: QUERY_RELATED_PRODUCT_BY_SKUS,
        variables: {
          ruleId: Number(storeConfig.popular_group_id),
          limit: 4,
        },
        context: { fetchOptions: { method: "GET" } },
      }),
      apolloClient.query({
        query: QUERY_RELATED_PRODUCT_BY_SKUS,
        variables: {
          ruleId: Number(storeConfig.trending_group_id),
          limit: 12,
        },
        context: { fetchOptions: { method: "GET" } },
      }),
    ]);

    const queryProductRelated = (queryProductRelatedRes as any)?.value?.data;
    const mostPopularProducts = (mostPopularProductsRes as any)?.value?.data;
    const trendingProducts = (trendingProductsRes as any)?.value?.data;

    const serverCmsBlocks = blockData.cmsBlocks.items.reduce(
      (acc: any, cur: any) => {
        acc[cur.identifier] = cur;
        return acc;
      },
      {} as any,
    );

    const featuredProductBlock = serverCmsBlocks[Identifiers.FeatureProduct];
    const featuredProductV2Block =
      serverCmsBlocks[Identifiers.FeaturedProductV2];
    const regexCustomVar = new RegExp("(?<=var tab-categories=).*?(?=}})", "g");
    const options =
      featuredProductBlock?.content?.match(regexCustomVar)?.[0] ?? "{}";
    const options2 =
      featuredProductV2Block?.content?.match(regexCustomVar)?.[0] ?? "{}";
    const result = JSON.parse(options);
    const result2 = JSON.parse(options2);

    // Fetch collection data with timeout
    const [dataCollectionSsrRes, dataCollectionV2SsrRes] =
      await Promise.allSettled([
        createTimeoutPromise(
          apolloClient.query({
            query: QUERY_GET_COLLECTIONS_BLOCKS,
            variables: {
              url_key: result[0]?.value ?? "",
              page_size: 3,
              current_page: 1,
            },
            context: { fetchOptions: { method: "GET" } },
          }),
          5000, // 5 second timeout
        ),
        createTimeoutPromise(
          apolloClient.query({
            query: QUERY_GET_COLLECTIONS_BLOCKS,
            variables: {
              url_key: result2[0]?.value ?? "",
              page_size: 5,
              current_page: 1,
            },
            context: { fetchOptions: { method: "GET" } },
          }),
          5000, // 5 second timeout
        ),
      ]);

    // Extract data from settled promises
    const dataCollectionSsr =
      (dataCollectionSsrRes as any)?.value?.data ?? null;
    const dataCollectionV2Ssr =
      (dataCollectionV2SsrRes as any)?.value?.data ?? null;

    return {
      props: {
        meta: data?.cmsPage ?? null,
        sliderItems: (fetchSlider as any)?.data?.getSlider?.items ?? null,
        configSchema: dataSchema?.MarketingConfig?.schemaHomepage ?? null,
        initialApolloState: apolloClient.cache.extract(),
        dataMegaMenu,
        storeConfig,
        serverCmsBlocks,
        bestSellerRelatedProductsBySkus:
          queryProductRelated?.relatedProductsBySkus ?? null,
        dataCollectionSsr,
        dataCollectionV2Ssr,
        mostPopularRelatedProductsBySkus:
          mostPopularProducts?.relatedProductsBySkus ?? null,
        trendingRelatedProductsBySkus:
          trendingProducts?.relatedProductsBySkus ?? null,
        siteReviews: siteReviews ?? null,
        isMobile,
      },
    };
  } catch (error) {
    return {
      props: {
        meta: {},
        storeConfig: {},
        cmsData: null,
        initialApolloState: null,
        cmsBlocks: [],
        homeStructs: [],
        bestSellerItems: [],
        relatedProductsBySkus: {},
        dataCollectionSsr: {},
      },
    };
  }
};
